// 효과 스프라이트 스펙(vfx-library v1 presets.ts의 SPEC을 게임 카메라·혼전에 맞춰 이식·추가).
// facing 규약은 게임과 같다: 방향 벡터 = (sin f, cos f). 함수(scale/opacity)는 모듈 로드 때 한 번 만든다.
import type { BlendKind } from './material';
import type { TexId } from './textures';

/** billboard: 카메라를 본다 / ground: 바닥에 눕는다 / pillar: 세로로 서서 카메라 쪽으로 돈다 / facing: 영웅 방향으로 눕혀 기울인다(참격) */
export type Orient = 'billboard' | 'ground' | 'pillar' | 'facing';

export interface SpriteSpec {
  tex: TexId;
  blend: BlendKind;
  orient: Orient;
  duration: number;
  /** 너비, 높이(m) */
  size: readonly [number, number];
  tint?: number;
  recolor?: number;
  intensity?: number;
  /** facing 전용: 바닥에서 들어 올리는 각도(rad) */
  tilt?: number;
  /** 획 그리기: uv 진행축, 그리는 시간, 소멸 시작, 소멸 시간(초) */
  reveal?: { axis: readonly [number, number]; draw: number; fadeAt: number; fade: number };
  /** u(0..1) → 배율 */
  scale?: (u: number) => number;
  /** u(0..1) → 불투명도 */
  opacity?: (u: number) => number;
  /** 면 법선 축 회전 속도(rad/s) */
  spin?: number;
  /** 위로 뜨는 속도(m/s) */
  rise?: number;
  /** 진행 방향(facing)으로 움직이는 속도(m/s) */
  move?: number;
  /** 시작 위치에서 이 오프셋만큼 내려온다. [앞, 위] m → 0까지 */
  fall?: readonly [number, number];
}

const easeOut = (u: number) => 1 - (1 - u) ** 3;
const fadeInOut = (inU: number, outU: number) => (u: number) => (u < inU ? u / inU : u > 1 - outU ? (1 - u) / outU : 1);
const pop = (peakU: number, peak: number) => (u: number) => (u < peakU ? 0.3 + (u / peakU) * (peak - 0.3) : peak * (1 - (u - peakU) / (1 - peakU)) + 0.05);
const grow = (u: number) => 0.25 + 0.75 * easeOut(u);
const fadeLin = (u: number) => 1 - u;
const streakIn = (u: number) => (u < 0.1 ? u / 0.1 : 1 - (u - 0.1) / 0.9);

export const SPEC = {
  // ── 라이브러리 원본(수치 유지) ─────────────────────────
  // 참격·룬은 톤매핑·블룸에서 분홍빛 흰색으로 바래므로 진홍으로 재채색한다(심은 밝기 4제곱 항으로 하얗게 남는다)
  slash: { tex: 'slash-crescent', blend: 'add', orient: 'facing', duration: 0.24, size: [4.2, 4.2], tilt: 0.5, intensity: 0.85, tint: 0xff1430, recolor: 0.75,
    reveal: { axis: [0.41, 0.59], draw: 0.09, fadeAt: 0.09, fade: 0.13 } },
  slashBig: { tex: 'slash-crescent', blend: 'add', orient: 'facing', duration: 0.3, size: [6.5, 6.5], tilt: 0.35, intensity: 0.95, tint: 0xff1430, recolor: 0.75,
    reveal: { axis: [0.41, 0.59], draw: 0.07, fadeAt: 0.1, fade: 0.18 } },
  // 각성 난무 참격: 6타가 0.9초 안에 겹치므로 더 짙은 진홍·낮은 세기(겹쳐도 분홍·흰 안개가 되지 않게, dream-loop 컷인 1회차)
  slashAwaken: { tex: 'slash-crescent', blend: 'add', orient: 'facing', duration: 0.26, size: [6.0, 6.0], tilt: 0.35, intensity: 0.55, tint: 0xc80520, recolor: 0.95,
    reveal: { axis: [0.41, 0.59], draw: 0.07, fadeAt: 0.09, fade: 0.17 } },
  flare: { tex: 'impact-flare', blend: 'add', orient: 'billboard', duration: 0.14, size: [1.6, 1.6], intensity: 1.2, scale: pop(0.2, 1.2) },
  flareSmall: { tex: 'impact-flare', blend: 'add', orient: 'billboard', duration: 0.12, size: [1.2, 1.2], intensity: 1.0, scale: pop(0.25, 1.1), tint: 0xffd2b0, recolor: 0.5 },
  ring: { tex: 'shockwave-ring', blend: 'add', orient: 'ground', duration: 0.35, size: [2, 2], scale: grow, opacity: fadeLin },
  dust: { tex: 'dust-puff', blend: 'alpha', orient: 'billboard', duration: 0.5, size: [1.4, 1.4], scale: (u: number) => 0.6 + u, opacity: (u: number) => 0.85 * (1 - u), rise: 0.7 },
  runeAwaken: { tex: 'rune-circle-crimson', blend: 'add', orient: 'ground', duration: 2.4, size: [7, 7], spin: 0.6, intensity: 0.7, tint: 0xff1a36, recolor: 0.9, opacity: fadeInOut(0.06, 0.15) },
  /** 회피 잔상: 바람 줄기를 제자리에 깔고 빠르게 지운다(영웅색 흰빛 진홍) */
  afterimage: { tex: 'wind-streak-teal', blend: 'add', orient: 'facing', duration: 0.26, size: [3.2, 1.1], tilt: 0, intensity: 0.8, tint: 0xffc4cc, recolor: 1,
    scale: (u: number) => 1 + 0.25 * u, opacity: streakIn },
  splat: { tex: 'crimson-splatter', blend: 'alpha', orient: 'ground', duration: 1.8, size: [1.7, 1.7], intensity: 0.95,
    scale: (u: number) => (u < 0.04 ? 0.5 + (u / 0.04) * 0.5 : 1), opacity: (u: number) => (u < 0.65 ? 1 : 1 - (u - 0.65) / 0.35) },

  // ── 게임 추가(전투 개편 2026-10-14) ────────────────────
  /** 막힘(방패 정면): 회색 금속 섬광·작은 원 */
  flareMetal: { tex: 'impact-flare', blend: 'add', orient: 'billboard', duration: 0.12, size: [0.95, 0.95], intensity: 0.9, scale: pop(0.25, 1.0), tint: 0xb8d0f0, recolor: 1 },
  ringMetal: { tex: 'shockwave-ring', blend: 'add', orient: 'billboard', duration: 0.22, size: [1.1, 1.1], intensity: 0.85, tint: 0xa9c0dc, recolor: 1, scale: grow, opacity: fadeLin },
  /** 저스트 회피 성공: 발밑 차가운 흰청 고리(2026-10-19 전투 가독성, 시안 c-dodge) */
  ringJust: { tex: 'shockwave-ring', blend: 'add', orient: 'ground', duration: 0.4, size: [2.4, 2.4], intensity: 1.3, tint: 0x9fd4ff, recolor: 1, scale: grow, opacity: fadeLin },
  /** 가드 브레이크: 진한 금빛 바닥 고리(막힘의 청백·처치의 진홍과 색으로 구별) */
  ringBreak: { tex: 'shockwave-ring', blend: 'add', orient: 'ground', duration: 0.42, size: [2, 2], intensity: 1.1, tint: 0xffb52e, recolor: 1, scale: grow, opacity: fadeLin },
  /** 영웅 피격: 붉은 작은 섬광 */
  flareHurt: { tex: 'impact-flare', blend: 'add', orient: 'billboard', duration: 0.12, size: [1.1, 1.1], intensity: 0.9, scale: pop(0.25, 1.0), tint: 0xff3a3a, recolor: 0.8 },
  /** 치명·마무리타: 타격 방향으로 몸을 꿰뚫는 진홍 줄기(콘셉트 c-hit-damage의 붉은 사선) */
  hitStreak: { tex: 'wind-streak-teal', blend: 'add', orient: 'facing', duration: 0.16, size: [2.8, 0.7], tilt: 0.25, intensity: 1.5, tint: 0xff2a3d, recolor: 1,
    scale: (u: number) => 0.7 + 0.6 * easeOut(u), opacity: fadeLin },
  /** 처치 땅 고리(가는 진홍) */
  ringKill: { tex: 'shockwave-ring', blend: 'add', orient: 'ground', duration: 0.32, size: [2, 2], intensity: 0.9, tint: 0xff5a64, recolor: 0.7, scale: grow, opacity: fadeLin },
  /** 큰 흙먼지(처치·착지) */
  dustBig: { tex: 'dust-puff', blend: 'alpha', orient: 'billboard', duration: 0.7, size: [2.2, 2.2], scale: (u: number) => 0.55 + 0.9 * easeOut(u), opacity: (u: number) => 0.8 * (1 - u), rise: 0.45 },
  /** 대량 처치·스킬 마무리 바깥 고리(따뜻한 흰색: 영웅색 + 흰 섬광 규칙) */
  ringGold: { tex: 'shockwave-ring', blend: 'add', orient: 'ground', duration: 0.45, size: [2, 2], intensity: 0.9, tint: 0xffe2d6, recolor: 1, scale: grow, opacity: fadeLin },
  /** 피의 돌진: 7m 경로를 따라 깔리는 진홍 줄기 */
  dashStreak: { tex: 'wind-streak-teal', blend: 'add', orient: 'facing', duration: 0.48, size: [8.0, 2.0], tilt: 0, intensity: 0.9, tint: 0xff1430, recolor: 1,
    scale: (u: number) => 0.85 + 0.15 * u, opacity: streakIn },
  /** 칼날 폭풍: 영웅 발밑 회전 룬(0.9초) */
  /** 칼날 폭풍 회차 참격: 판정 반경 5m를 덮는 납작한 큰 초승달 */
  slashStorm: { tex: 'slash-crescent', blend: 'add', orient: 'facing', duration: 0.26, size: [4.2, 4.2], tilt: 0.18, intensity: 1.05, tint: 0xff1430, recolor: 0.7,
    reveal: { axis: [0.41, 0.59], draw: 0.08, fadeAt: 0.1, fade: 0.14 } },
  runeStorm: { tex: 'rune-circle-crimson', blend: 'add', orient: 'ground', duration: 1.0, size: [10, 10], spin: 2.6, intensity: 0.6, tint: 0xff1a36, recolor: 0.9,
    scale: (u: number) => 0.8 + 0.2 * easeOut(Math.min(1, u * 4)), opacity: fadeInOut(0.1, 0.25) },
  /** 붉은 낙화: 착지점 룬(도약 0.65초 동안) */
  runeFall: { tex: 'rune-circle-crimson', blend: 'add', orient: 'ground', duration: 0.72, size: [9, 9], spin: 1.5, intensity: 0.7, tint: 0xff1a36, recolor: 0.9,
    scale: (u: number) => 0.55 + 0.45 * easeOut(u), opacity: fadeInOut(0.15, 0.1) },

  // ── 결투장 측정 적용(docs/analysis/DUELING-GROUNDS.md, 2026-10-14) ──
  /**
   * 임팩트 별: 한 동작의 첫 타격 순간 몸 앞에 서는 흰 4방향 별(화면 높이 약 1/6).
   * 영웅 시간으로 흐르므로 히트스톱 동안 그대로 서 있다가 정지가 풀리면 2틱 만에 사라진다(결투장의 1프레임 별).
   */
  impactStar: { tex: 'impact-flare', blend: 'add', orient: 'billboard', duration: 0.035, size: [2.3, 2.3], intensity: 2.2, tint: 0xfff4ea, recolor: 0.92 },
  /** KO 도장: 붉은 X(빛 X 참격 원화를 진홍으로 다시 칠한다). 정지가 풀린 뒤 찍혀 0.5초 안에 사라진다 */
  koStamp: { tex: 'shadow-x-slash', blend: 'add', orient: 'billboard', duration: 0.5, size: [3.2, 3.2], intensity: 1.4, tint: 0xff1a2a, recolor: 1,
    scale: pop(0.12, 1.15), opacity: (u: number) => (u < 0.7 ? 1 : (1 - u) / 0.3) },

  // ── 스킬 트리(2026-10-20, docs/SKILL-TREE.md 4~5장, 시안 concept/skill-tree-20261020/vfx-*.webp) ─────────
  // 시안보다 얌전하게: 강도를 낮추고 바닥 흔적은 짧게 지워 혼잡 웨이브에서도 영웅·적 실루엣과 바닥 절반 이상이 읽히게 한다.
  /** 스킬 4 진홍 초승달 본체: 길이 8m를 0.3초에 난다(move = 8m / 0.3s). 가장자리는 서서히 켜지고 끝에서 사라진다 */
  crescentWave: { tex: 'crescent-wave', blend: 'add', orient: 'facing', duration: 0.32, size: [5.8, 5.8], tilt: 0.95, intensity: 0.42, tint: 0xff1030, recolor: 0.85,
    move: 26.7, scale: (u: number) => 0.72 + 0.28 * easeOut(Math.min(1, u * 3)), opacity: (u: number) => (u < 0.12 ? u / 0.12 : u > 0.72 ? (1 - u) / 0.28 : 1) },
  /** 초승달 몸통: 앞날(흰 심)은 본체가 맡고, 이쪽은 같은 모양을 짙은 진홍으로 깔아 두께(채도)를 준다 */
  crescentBody: { tex: 'crescent-wave', blend: 'alpha', orient: 'facing', duration: 0.32, size: [5.4, 5.4], tilt: 0.95, intensity: 1.0, tint: 0xd8081e,
    move: 26.7, scale: (u: number) => 0.72 + 0.28 * easeOut(Math.min(1, u * 3)), opacity: (u: number) => 0.9 * (u < 0.12 ? u / 0.12 : u > 0.72 ? (1 - u) / 0.28 : 1) },
  /** 초승달 잔상: 본체보다 뒤에서 더 어둡고 넓게 남는다 */
  crescentGhost: { tex: 'crescent-wave', blend: 'add', orient: 'facing', duration: 0.3, size: [6.4, 6.4], tilt: 0.95, intensity: 0.5, tint: 0xc00018, recolor: 1,
    move: 26.7, scale: (u: number) => 0.8 + 0.2 * u, opacity: (u: number) => (u < 0.15 ? u / 0.15 : (1 - u) / 0.85) },
  /** 쌍월(두 번째 초승달): 첫 번째보다 작고 살짝 낮게 나가 교차하는 것처럼 보인다 */
  crescentTwin: { tex: 'crescent-wave', blend: 'add', orient: 'facing', duration: 0.32, size: [5.2, 5.2], tilt: 0.85, intensity: 0.5, tint: 0xff1a34, recolor: 0.85,
    move: 26.7, scale: (u: number) => 0.72 + 0.28 * easeOut(Math.min(1, u * 3)), opacity: (u: number) => (u < 0.12 ? u / 0.12 : u > 0.72 ? (1 - u) / 0.28 : 1) },
  /** 바닥 긁힌 자국(길이를 scale, 폭을 stretch로 맞춘다): 앞에서 뒤로 그려지고 뒤쪽부터 지워진다 */
  scarCrimson: { tex: 'ground-fissure', blend: 'add', orient: 'facing', duration: 0.95, size: [1, 1], tilt: 0, intensity: 2.0, tint: 0xff1428, recolor: 0.25,
    reveal: { axis: [0, 1], draw: 0.3, fadeAt: 0.3, fade: 0.6 }, opacity: (u: number) => (u < 0.5 ? 1 : (1 - u) / 0.5) },
  /** 적중한 적 몸에 뜨는 작은 진홍 섬광(초승달이 지나가는 동안의 스킬 타격) */
  flareCrimson: { tex: 'impact-flare', blend: 'add', orient: 'billboard', duration: 0.16, size: [1.5, 1.5], intensity: 1.0, tint: 0xff1a30, recolor: 0.85, scale: pop(0.25, 1.1), opacity: fadeLin },
  /** 핏빛 궤적(돌진 경로 균열): 짧게 터지듯 그려진다 */
  scarDash: { tex: 'ground-fissure', blend: 'add', orient: 'facing', duration: 0.8, size: [1, 1], tilt: 0, intensity: 1.5, tint: 0xff1a30, recolor: 0.4,
    reveal: { axis: [0, 1], draw: 0.14, fadeAt: 0.2, fade: 0.6 }, opacity: (u: number) => (u < 0.4 ? 1 : (1 - u) / 0.6) },
  /** 돌진 경로에서 솟는 진홍 가시(번개 칼날 텍스처를 진홍으로 다시 칠해 위로 세운다) */
  spikeCrimson: { tex: 'lightning-blade', blend: 'alpha', orient: 'billboard', duration: 0.4, size: [2.55, 3.0], intensity: 2.2, tint: 0xff1020,
    scale: (u: number) => (u < 0.22 ? 0.2 + 0.8 * easeOut(u / 0.22) : 1 - 0.25 * (u - 0.22)), opacity: (u: number) => 0.95 * (u < 0.6 ? 1 : (1 - u) / 0.4) },
  /** 폭풍의 눈: 소용돌이가 한 점으로 모인다(원 반경 = size/2 × scale) */
  eyeVortex: { tex: 'vortex-swirl', blend: 'add', orient: 'ground', duration: 0.34, size: [2, 2], spin: 11, intensity: 0.62,
    scale: (u: number) => 1 - 0.88 * u * u, opacity: (u: number) => (u < 0.18 ? u / 0.18 : u > 0.75 ? (1 - u) / 0.25 : 1) },
  /** 폭풍의 눈 터짐: 한 점에서 반경까지 퍼지는 고리 한 겹(진홍·흰 가장자리) */
  eyeRing: { tex: 'shockwave-ring', blend: 'add', orient: 'ground', duration: 0.55, size: [2, 2], intensity: 1.6, tint: 0xff3a4c, recolor: 1, scale: grow, opacity: fadeLin },
  /** 여진: 금빛 호박색 고리 */
  ringAmber: { tex: 'shockwave-ring', blend: 'add', orient: 'ground', duration: 0.5, size: [2, 2], intensity: 1.25, tint: 0xffa21f, recolor: 1, scale: grow, opacity: fadeLin },
  /** 여진 방사 균열(길이 = scale, 폭 = stretch): 중심에서 바깥으로 그려지고 안쪽부터 사라진다 */
  crackAmber: { tex: 'ground-fissure', blend: 'add', orient: 'facing', duration: 0.85, size: [1, 1], tilt: 0, intensity: 1.8, tint: 0xffa21f, recolor: 0.7,
    reveal: { axis: [0, 1], draw: 0.2, fadeAt: 0.25, fade: 0.55 }, opacity: (u: number) => (u < 0.45 ? 1 : (1 - u) / 0.55) },
  /** 진홍 개화: 바닥에 퍼지는 꽃잎·칼날 조각 방사(반경 = size/2 × scale) */
  bloomGround: { tex: 'petal-burst', blend: 'alpha', orient: 'ground', duration: 0.62, size: [2, 2], spin: 0.7, intensity: 1.0, tint: 0xff2a3c,
    scale: (u: number) => 0.3 + 0.7 * easeOut(Math.min(1, u * 1.8)), opacity: (u: number) => 0.9 * (u < 0.1 ? u / 0.1 : (1 - u) / 0.9) },
  /** 개화 룬 꽃 데칼(짧게) */
  runeBloom: { tex: 'rune-circle-crimson', blend: 'add', orient: 'ground', duration: 0.7, size: [2, 2], spin: 0.9, intensity: 0.5, tint: 0xff1a36, recolor: 0.9,
    scale: (u: number) => 0.55 + 0.45 * easeOut(Math.min(1, u * 2.5)), opacity: fadeInOut(0.1, 0.5) },
  /** 뇌명 검기 본체: 청백 번개 칼날이 앞으로 나간다(move = 6m / 0.18s) */
  thunderBlade: { tex: 'lightning-blade', blend: 'add', orient: 'facing', duration: 0.2, size: [2.7, 4.6], tilt: 0.5, intensity: 1.15,
    move: 33, scale: (u: number) => 0.85 + 0.15 * u, opacity: (u: number) => (u < 0.1 ? u / 0.1 : u > 0.7 ? (1 - u) / 0.3 : 1) },
  thunderGhost: { tex: 'lightning-blade', blend: 'add', orient: 'facing', duration: 0.18, size: [3.1, 5.2], tilt: 0.5, intensity: 0.32, tint: 0x6aa0ff, recolor: 0.8,
    move: 33, opacity: (u: number) => (u < 0.15 ? u / 0.15 : (1 - u) / 0.85) },
  /** 뇌명 짧은 지면 번개(바닥에 눕는다) */
  arcBlue: { tex: 'ground-fissure', blend: 'add', orient: 'facing', duration: 0.26, size: [1, 1], tilt: 0, intensity: 1.9, tint: 0x8fb8ff, recolor: 0.8,
    opacity: (u: number) => (u < 0.2 ? u / 0.2 : (1 - u) / 0.8) },
  /** 레벨 업 고리 박동 한 번(부드럽게 퍼지는 금빛) */
  ringLevel: { tex: 'shockwave-ring', blend: 'add', orient: 'ground', duration: 1.0, size: [2, 2], intensity: 0.95, tint: 0xffc84a, recolor: 1, scale: grow, opacity: (u: number) => (u < 0.15 ? u / 0.15 : (1 - u) / 0.85) },
} as const satisfies Record<string, SpriteSpec>;

export type SpecName = keyof typeof SPEC;

/** 묶음 크기(텍스처당 동시 최대 장 수). 넘치면 버린다. */
export const BATCH_CAP: Partial<Record<TexId, number>> = {
  'impact-flare': 48,
  'shockwave-ring': 40,
  'slash-crescent': 24,
  'wind-streak-teal': 24,
  'rune-circle-crimson': 4,
  'dust-puff': 48,
  'crimson-splatter': 32,
  'shadow-x-slash': 4,
  'crescent-wave': 14,
  'petal-burst': 6,
  'lightning-blade': 24,
  'ground-fissure': 24,
  'vortex-swirl': 3,
};

